import * as React from 'react';
import styled from 'styled-components';
import { HomeWorkOutlined, EmailOutlined, PhoneOutlined, BusinessOutlined } from '@material-ui/icons';
import UsersForm from '../forms/UsersForm';
import { ModalDefaultDraggable } from '../molecules';
import useTablePage from '../hooks/useTablePage';
import { getEmployeesUsers, getUserByID, updateUser } from '../api/users';
import ModalInfo from '../molecules/ModalInfo';
import { GET_ALL_QUERY_PARAMS } from '../constants';
import { CLIENT_TYPES_VALUES } from '../constants/forms';
import { getContactTypeList } from '../api/contactType';
import ContactsTable from './Tables/ContactsTable';
import UserPermitsModal from './UserPermitsModal';
import Loader from '../atoms/Loader';

const PermitViewContacts = ({
  permit,
  setContactUpdated,
  noEdit,
  cameFromSearch,
  selectedPermit,
}: Props) => {
  const {
    createOrUpdate,
    isModalOpen,
    modalTitle,
    modalError,
    modalSuccess,
    modalErrorTitle,
    handleCloseModal,
    handleCloseModalInfo,
    values,
    errors,
    handleEdit,
    setSearch,
    search,
    tableManuallyUpdate,
    updateTable,
  } = useTablePage({
    updateEndpoint: updateUser,
    modalUpdateTitle: 'Contact Details',
  });
  const [contactTypesData, setContactTypesData] = React.useState(null);

  React.useEffect(() => {
    getContactTypeList({
      ...GET_ALL_QUERY_PARAMS,
    }).then(contactTypesResponse => {
      const activeContactTypes = contactTypesResponse?.data?.results?.filter(type => type?.isActiveStatus === "True");
      setContactTypesData(activeContactTypes);
    });
  }, []);

  React.useEffect(() => {
    if (modalSuccess) {
      updateTable();
      setContactUpdated(true);
    }
  }, [modalSuccess]);

  const modifiedGetContacts = () =>
    Promise.all([
      getEmployeesUsers({ user_role: 3, permit_id: permit }),
      selectedPermit?.contractor && getUserByID(selectedPermit?.contractor),
    ])
      .then(response => {
        const contacts = response[0]?.data?.filter(
          el => el.isActive,
        );
        let contractor = {};
        if (selectedPermit?.contractor) {
          contractor = response[1]?.data;
        }

        return {
          ...response[0],
          data: {
            ...response[0]?.data || [],
            results: (Object.keys(contractor)?.length > 0 ? [...contacts, contractor] : contacts) || [],
          },
        };
      })
      .catch(err => console.log(err))
      .finally(() => {});

  const handleCloseUserForm = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  const handleCloseSuccessModal = () => {
    setContactUpdated(false);
    handleCloseModalInfo();
  };
const [permitUserId, setPermitUserId] = React.useState(null);
  const [showUserPermitsModal, setShowUserPermitsModal] = React.useState(false);
const viewUserPermits = row => {
  setPermitUserId(row?.id);
  setShowUserPermitsModal(true);
};

const handleCloseUserPermitsModal = () => {
  setShowUserPermitsModal(false);
};
  const [contacts, setContacts] = React.useState<any[]>([]);
  const [contactsLoading, setContactsLoading] = React.useState(false);

  React.useEffect(() => {
    if (!noEdit) return;
    setContactsLoading(true);
    Promise.resolve(modifiedGetContacts({}))
      .then((res: any) => setContacts(res?.data?.results || []))
      .finally(() => setContactsLoading(false));
  }, [noEdit, permit, selectedPermit?.contractor]);

  const filteredContacts = React.useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(c => {
      const haystack = [
        c?.firstName, c?.lastName, c?.companyName, c?.contactType,
        c?.phoneNumber, c?.mobilePhoneNumber, c?.email,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [contacts, search]);

  return (
    <ContactsWrapper>
      {noEdit ? (
        <ContactList aria-label="Permit contacts">
          {contactsLoading && (
            <ListState>
              <Loader size={32} />
              <ListStateText>Loading contacts…</ListStateText>
            </ListState>
          )}
          {!contactsLoading && filteredContacts.length === 0 && (
            <ListState>
              <ListStateText>No contacts found.</ListStateText>
            </ListState>
          )}
          {!contactsLoading && filteredContacts.map((c: any) => {
            const fullName = [c?.firstName, c?.lastName].filter(Boolean).join(' ');
            const heading = fullName || c?.companyName || '—';
            const subheading = fullName ? c?.companyName : null;
            return (
              <ContactCard key={c?.id || `${heading}-${c?.email}`}>
                <CardHead>
                  <CardHeading>
                    <CardName title={heading}>{heading}</CardName>
                    {subheading && (
                      <CardCompany title={subheading}>
                        <BusinessOutlined fontSize="inherit" />
                        <span>{subheading}</span>
                      </CardCompany>
                    )}
                  </CardHeading>
                  <CardBadges>
                    {c?.contactType && (
                      <Chip $tone="neutral">{c.contactType}</Chip>
                    )}
                    {c?.isAccounting && (
                      <Chip $tone="success">Accounting</Chip>
                    )}
                    {c?.isInspection && (
                      <Chip $tone="info">Inspection</Chip>
                    )}
                  </CardBadges>
                </CardHead>
                <CardMeta>
                  {c?.phoneNumber && (
                    <MetaItem>
                      <PhoneOutlined fontSize="inherit" />
                      <MetaValue>{c.phoneNumber}</MetaValue>
                    </MetaItem>
                  )}
                  {c?.email && (
                    <MetaItem>
                      <EmailOutlined fontSize="inherit" />
                      <MetaValue
                        as="a"
                        href={`mailto:${c.email}`}
                        title={c.email}
                      >
                        {c.email}
                      </MetaValue>
                    </MetaItem>
                  )}
                </CardMeta>
                <CardActions>
                  <CardActionButton
                    type="button"
                    onClick={() => viewUserPermits(c)}
                    title="View Permits"
                    aria-label="View Permits"
                  >
                    <HomeWorkOutlined fontSize="small" />
                    <span>View Permits</span>
                  </CardActionButton>
                </CardActions>
              </ContactCard>
            );
          })}
        </ContactList>
      ) : null}
      {(!noEdit && contactTypesData) ? (
        <div style={{ maxHeight: '800px', overflow: 'auto' }}>
          <ContactsTable
            setShowUserPermitsModal={(id) => {
              setPermitUserId(id);
              setShowUserPermitsModal(true);
            }}
            dataRows={modifiedGetContacts}
            handleEdit={handleEdit}
            manuallyUpdate={tableManuallyUpdate}
          />
        </div>
      ) : null}

      {isModalOpen && (
        <StyledModalDraggable
          onClose={handleCloseUserForm}
          title={
            Number(values?.clientType) === CLIENT_TYPES_VALUES.INDIVIDUAL
              ? 'Individual Details'
              : Number(values?.clientType) === CLIENT_TYPES_VALUES.COMPANY
              ? 'Company Details'
              : modalTitle
          }
          wide
        >
          <UsersForm
            // setIsContactFormSubmitting={null}
            initialValues={values}
            modalError={modalErrorTitle.length > 0}
            initialErrors={errors}
            onSubmit={createOrUpdate}
            hideRoleAndType={
              Number(values?.clientType) === CLIENT_TYPES_VALUES.INDIVIDUAL ||
              Number(values?.clientType) === CLIENT_TYPES_VALUES.COMPANY
            }
            hideParentCompany={
              Number(values?.clientType) === CLIENT_TYPES_VALUES.INDIVIDUAL
            }
            companyMode={
              Number(values?.clientType) === CLIENT_TYPES_VALUES.COMPANY
            }
            updateButtonText={
              Number(values?.clientType) === CLIENT_TYPES_VALUES.INDIVIDUAL
                ? 'Update Individual'
                : Number(values?.clientType) === CLIENT_TYPES_VALUES.COMPANY
                ? 'Update Company'
                : 'Update Contact'
            }
            cameFromSearch={cameFromSearch}
          />
        </StyledModalDraggable>
      )}

      {showUserPermitsModal && (
        <UserPermitsModal
          userId={permitUserId}
          onClose={handleCloseUserPermitsModal}
        />
      )}

      {(modalSuccess || modalError) && (
        <ModalInfo
          timeToClose={5000}
          isError={modalError}
          onClose={handleCloseSuccessModal}
          errorMsg={modalErrorTitle}
        />
      )}
    </ContactsWrapper>
  );
};

interface Props {
  permit: number;
  setContactUpdated: (data: boolean) => void;
  noEdit?: boolean;
  cameFromSearch?: boolean;
}

const StyledModalDraggable = styled(ModalDefaultDraggable)`
  height: 100%;
  overflow: inherit;
`;

const ContactsWrapper = styled.div`
  min-height: 200px;
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 4px;
`;

const ListState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 16px;
  color: var(--neutral-500);
`;

const ListStateText = styled.div`
  font-size: 0.875rem;
`;

const ContactCard = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'head head'
    'meta actions';
  gap: 8px 16px;
  padding: 14px 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-0);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--neutral-300);
    box-shadow: var(--shadow-xs);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'head'
      'meta'
      'actions';
  }
`;

const CardHead = styled.div`
  grid-area: head;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const CardHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CardName = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardCompany = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: var(--neutral-600);
  line-height: 1.3;
  min-width: 0;

  & > svg {
    flex: 0 0 auto;
    font-size: 14px;
    color: var(--neutral-500);
  }
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const CardBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const Chip = styled.span<{ $tone: 'neutral' | 'success' | 'info' }>`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border: 1px solid;
  white-space: nowrap;

  ${({ $tone }) => {
    switch ($tone) {
      case 'success':
        return `
          background: var(--success-50, #ecfdf5);
          color: var(--success-700, #047857);
          border-color: var(--success-100, #d1fae5);
        `;
      case 'info':
        return `
          background: var(--primary-50, #eff6ff);
          color: var(--primary-700, #1d4ed8);
          border-color: var(--primary-100, #dbeafe);
        `;
      default:
        return `
          background: var(--neutral-50);
          color: var(--neutral-700);
          border-color: var(--border);
        `;
    }
  }}
`;

const CardMeta = styled.div`
  grid-area: meta;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  font-size: 0.8125rem;
  color: var(--neutral-700);
  align-items: center;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  & > svg {
    flex: 0 0 auto;
    font-size: 15px;
    color: var(--neutral-500);
  }
`;

const MetaValue = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 320px;
  color: inherit;
  text-decoration: none;

  &[href]:hover {
    color: var(--primary-700);
    text-decoration: underline;
  }
`;

const CardActions = styled.div`
  grid-area: actions;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const CardActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  white-space: nowrap;

  & > svg {
    color: var(--neutral-500);
    transition: color 0.15s ease;
  }

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary-200, var(--primary-100));
    color: var(--primary-700);
  }
  &:hover > svg {
    color: var(--primary-600);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

export default PermitViewContacts;
